<template>
  <div class="vc-email-form">
    <div class="vc-email-form__field" :class="emailStatusClass">
      <v-text-field
        v-model="emailInput"
        :label="$t('message.sichernConnect.modal.bodyNoEmail.placeholder')"
        outline
        hide-details
        @blur="emailTouched = true"
        @input="onEmailInput"
      >
        <template v-if="emailInput" v-slot:append>
          <status-icon :status="isEmailFormatValid ? 'success' : 'error'" />
        </template>
      </v-text-field>
      <span v-if="emailErrorMessage" class="vc-email-form__error">
        <status-icon status="error-outlined" size="small" />
        {{ emailErrorMessage }}
      </span>
    </div>

    <div v-if="showPasswordField" class="vc-email-form__field">
      <v-text-field
        v-model="passwordInput"
        :label="$t('message.sichernConnect.modal.bodyNoEmail.passwordPlaceholder')"
        type="password"
        outline
        hide-details
        @blur="passwordTouched = true"
        @input="onPasswordInput"
      />
      <span v-if="passwordErrorMessage" class="vc-email-form__error">
        <status-icon status="error-outlined" size="small" />
        {{ passwordErrorMessage }}
      </span>
    </div>
  </div>
</template>

<script>
import StatusIcon from './StatusIcon.vue'

var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/

export default {
  name: 'SichernConnectEmailForm',
  components: { StatusIcon },
  data () {
    return {
      emailInput: '',
      emailTouched: false,
      emailSubmitted: false,
      passwordInput: '',
      passwordTouched: false
    }
  },
  computed: {
    isEmailFormatValid () {
      return EMAIL_REGEX.test(this.emailInput)
    },
    showPasswordField () {
      return this.isEmailFormatValid
    },
    shouldValidateEmail () {
      return this.emailTouched || this.emailSubmitted
    },
    showEmailRequired () {
      return this.shouldValidateEmail && this.emailInput === ''
    },
    showEmailInvalid () {
      return this.emailInput !== '' && !this.isEmailFormatValid
    },
    showPasswordRequired () {
      return (this.passwordTouched || this.emailSubmitted) && this.passwordInput === ''
    },
    apiErrors () {
      return this.$store.state.sichernConnect.apiErrors
    },
    emailApiErrorMessage () {
      var code = this.apiErrors.emailCode
      if (!code) return ''
      var errorMsg = this.$t('message.selfService.emailForm.' + code)
      if (errorMsg && errorMsg !== 'message.selfService.emailForm.' + code) return errorMsg
      return this.$t('message.errors.' + code) || ''
    },
    passwordApiErrorMessage () {
      var code = this.apiErrors.passwordCode
      if (!code) return ''
      var errorMsg = this.$t('message.selfService.emailForm.' + code)
      if (errorMsg && errorMsg !== 'message.selfService.emailForm.' + code) return errorMsg
      return ''
    },
    emailErrorMessage () {
      if (this.showEmailRequired) return this.$t('message.sichernConnect.modal.bodyNoEmail.errorRequired')
      if (this.showEmailInvalid) return this.$t('message.sichernConnect.modal.bodyNoEmail.errorInvalid')
      return this.emailApiErrorMessage
    },
    passwordErrorMessage () {
      if (this.showPasswordRequired) return this.$t('message.sichernConnect.modal.bodyNoEmail.errorRequired')
      return this.passwordApiErrorMessage
    },
    emailStatusClass () {
      if (!this.emailInput) return ''
      return this.isEmailFormatValid && !this.emailErrorMessage ? 'email-valid' : 'email-invalid'
    }
  },
  methods: {
    onEmailInput (value) {
      this.emailInput = value.trim()
      this.$store.dispatch('sichernConnect/clearApiError', 'emailCode')
      this.$emit('update:canSubmit', this.isEmailFormatValid && this.passwordInput.length > 0)
    },
    onPasswordInput (value) {
      this.passwordInput = value.trim()
      this.$store.dispatch('sichernConnect/clearApiError', 'passwordCode')
      this.$emit('update:canSubmit', this.isEmailFormatValid && this.passwordInput.length > 0)
    },
    validate () {
      this.emailSubmitted = true
      this.passwordTouched = true
      if (!this.isEmailFormatValid || this.passwordInput === '') return null
      return { email: this.emailInput, password: this.passwordInput }
    }
  }
}
</script>

<style scoped lang="scss">
.vc-email-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  &__field {
    display: flex;
    flex-direction: column;
  }

  &__error {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    font-size: 0.75rem;
    color: #404040;
  }
}

.vc-email-form__field ::v-deep .v-input__slot {
  min-height: 46px !important;
  height: 46px;
}

.vc-email-form__field ::v-deep .v-label {
  top: 13px;
}

.vc-email-form__field ::v-deep .v-label--active {
  transform: translateY(-23px) scale(0.75);
  background: #FFF;
  padding: 0 5px;
}

.vc-email-form__field ::v-deep input {
  margin-top: auto;
  margin-bottom: auto;
  padding-top: 0;
  padding-bottom: 0;
}

.vc-email-form__field ::v-deep .v-input__slot {
  border: 1px solid rgba(0, 0, 0, 0.38) !important;
}

.vc-email-form__field ::v-deep .v-label {
  color: rgba(0, 0, 0, 0.54);
}

.vc-email-form__field ::v-deep .v-input--is-focused .v-input__slot {
  border-color: #4f41a3 !important;
}

.vc-email-form__field ::v-deep .v-input--is-focused .v-label {
  color: #4f41a3 !important;
}

.email-valid ::v-deep .v-input__slot {
  border-color: #027A55 !important;
}

.email-valid ::v-deep .v-label {
  color: #027A55 !important;
}

.email-invalid ::v-deep .v-input__slot {
  border-color: #B41818 !important;
}

.email-invalid ::v-deep .v-label {
  color: #B41818 !important;
}
</style>
